import { useParams } from 'react-router-dom';
import { workAreaReferenceFromParams } from '@featit/shared/addressing/paths';
import {
  workAreaAddressHoldsOf,
  type IWorkAreaAddressHold,
  type IWorkAreaCandidate,
  type WorkAreaResolutionType,
} from '@featit/shared/addressing/workAreaReference';
import {
  resolveWorkAreaRouteRef,
  workAreaPermalinkRef,
  workAreaRouteRef,
} from '@featit/shared/addressing/workAreaRouteRef';
import type { IProject } from '@featit/shared';

/**
 * The work area in a web address, with its namespace (I-0656, AC-M19; R-5, F-0250).
 *
 * ⭐ **A route reference is `acme/platform/api`**: the namespace path and the slug. The address is
 * `/acme/platform/api/-/items`, matched by one route per namespace depth (`workAreaAddressRoutes`), and
 * `useWorkAreaRouteRef` reads the reference back from whichever depth matched. Every link helper that
 * takes a `projectSlug` is given one of these, never `project.slug`, and every page resolves it here,
 * never with `projects.find((p) => p.slug === …)`.
 *
 * A bare `/p/tracker` from before the cutover (an old link, a desktop tab) is resolved by AC-M18 in
 * `LegacyAddressRedirect`: it resolves only when exactly one work area answers, and shows the
 * candidates otherwise.
 */

/**
 * The work-area reference of the address on screen, `acme/platform/api`, or `undefined` outside a
 * work area. ⛔ The one way a page reads it: the parameters differ per depth (`ns1` … `ns10`,
 * `workArea`), and a page that read one of them would see a slug without its namespace.
 */
export function useWorkAreaRouteRef(): string | undefined {
  return workAreaReferenceFromParams(useParams());
}

/** The route reference for a work area, or `undefined` when there is none. */
export function workAreaRouteRefOf(
  project:
    | (Pick<IProject, 'slug' | 'namespace'> & Partial<Pick<IProject, 'namespaceKey'>>)
    | null
    | undefined,
): string | undefined {
  return project ? workAreaRouteRef(project) : undefined;
}

/**
 * The key-form reference, for a link somebody will keep (D-0076, AC-K12): copy-link, a notification.
 * It survives a handle change. Navigation inside the app keeps the displayed form.
 */
export function workAreaPermalinkRefOf(
  project:
    | (Pick<IProject, 'slug' | 'namespace'> & Partial<Pick<IProject, 'namespaceKey'>>)
    | null
    | undefined,
): string | undefined {
  return project ? workAreaPermalinkRef(project) : undefined;
}

/** What resolving needs of a work area: its slug and name, and its namespace when known. */
export type WorkAreaRouteSourceType = Pick<IProject, 'slug' | 'name'> &
  Partial<Pick<IProject, 'id' | 'namespace' | 'namespaceKey' | 'formerAddresses'>>;

/** A loaded work area as the shared resolver reads it; `index` stands in for a missing id. */
function candidateOf(
  project: WorkAreaRouteSourceType,
  index: number,
): IWorkAreaCandidate & Pick<IProject, 'formerAddresses'> {
  return {
    id: project.id ?? `#${index}`,
    name: project.name,
    slug: project.slug,
    namespace: project.namespace ?? undefined,
    // I-0681, AC-K8: `/<key>/tracker` names the work area whatever the handle is.
    namespaceKey: project.namespaceKey ?? undefined,
    // I-1052: a rename or a move leaves this here, read by `routeHoldsOf` below.
    formerAddresses: project.formerAddresses ?? [],
  };
}

/**
 * Every unexpired hold a list of route candidates carries (I-1052), for the shared resolver's
 * `holds` parameter — the web app's own `workAreaAddressHoldsOf` call, kept beside `candidateOf`
 * so every caller of `resolveWorkAreaRouteRef`/`resolveRouteWorkArea` passes the same list.
 */
function routeHoldsOf(
  candidates: readonly (IWorkAreaCandidate & Pick<IProject, 'formerAddresses'>)[],
): IWorkAreaAddressHold[] {
  return workAreaAddressHoldsOf(candidates);
}

/**
 * Resolve a route reference against the loaded work areas: resolved, moved, not found, or
 * ambiguous (I-1052 AC-2: a route reference naming an address a rename or a move vacated resolves
 * through the hold each work area's own `formerAddresses` carries, rather than "not found").
 */
export function resolveRouteWorkArea(
  projects: readonly WorkAreaRouteSourceType[],
  routeRef: string,
): WorkAreaResolutionType {
  const candidates = projects.map(candidateOf);
  return resolveWorkAreaRouteRef(candidates, routeRef, routeHoldsOf(candidates));
}

/**
 * The work area a route reference names, or `undefined` when it names none **or several**. ⛔ Never
 * the first of several: an ambiguous reference is `WorkAreaRouteBoundary`'s to explain.
 */
export function findWorkAreaByRouteRef<T extends WorkAreaRouteSourceType>(
  projects: readonly T[],
  routeRef: string | null | undefined,
): T | undefined {
  if (!routeRef) return undefined;
  const candidates = projects.map(candidateOf);
  const resolved = resolveWorkAreaRouteRef(candidates, routeRef, routeHoldsOf(candidates));
  if (resolved.outcome !== 'resolved' && resolved.outcome !== 'moved') return undefined;
  const index = candidates.findIndex((candidate) => candidate.id === resolved.workArea.id);
  return projects[index];
}

/**
 * The work areas the reader can open in a namespace and in its subgroups, by handle path or key.
 * `acme` holds `acme/tracker` and `acme/platform/api`; `acme/platform` holds only the second.
 */
export function workAreasInNamespace<T extends Pick<IProject, 'namespace' | 'namespaceKey'>>(
  projects: readonly T[],
  namespace: string,
): T[] {
  const wanted = namespace.replace(/^\/+|\/+$/g, '');
  if (wanted === '') return [];
  return projects.filter(
    (project) =>
      project.namespaceKey === wanted ||
      project.namespace === wanted ||
      Boolean(project.namespace?.startsWith(`${wanted}/`)),
  );
}

/**
 * The address as a "no work area here" page may quote it (UXA-4): as given when its namespace is
 * one the reader's listing names, or when it names none (a bare slug); `null` otherwise. A guest's
 * work area in a personal namespace is listed by its key alone, so an old address in the owner's
 * handle is not read back to the guest; a namespace the reader knows is quoted as before.
 */
export function quotedMissingReference<T extends Pick<IProject, 'namespace' | 'namespaceKey'>>(
  reference: string,
  projects: readonly T[],
): string | null {
  const cut = reference.lastIndexOf('/');
  if (cut <= 0) return reference;
  return workAreasInNamespace(projects, reference.slice(0, cut)).length > 0 ? reference : null;
}
